import { PurchaseGuide } from "../components/PurchaseGuide";
import { usePurchasePreference } from "../hooks/usePurchasePreference";
import {
  type ChangeEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { CartPhotoEditorModal } from "../components/CartPhotoEditorModal";
import { convertDecimalToDMS, getLatLngFromBlockNumber, integerToBlock } from "../earth/pixel";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useLocale } from "../contexts/LocaleContext";
import { resolvePrice } from "../lib/price";
import { useToast } from "../contexts/ToastContext";
import type { Pixel } from "../types";

const DEFAULT_FRAME_IMG = "/static/img/gal12.jpg";

const ACCEPT_IMAGE = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

export function CartPage() {
  const { items, total, count, unreserve, patchItem } = useCart();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [stayOnMap, setStayOnMap] = usePurchasePreference("stay-on-map");
  const toast = useToast();
  const { t, formatMoney } = useLocale();

  /** Pixels we already prefilled (or that had a name) — avoids clobbering manual clears. */
  const artistPrefillDoneRef = useRef(new Set<number>());

  useEffect(() => {
    if (!isAuthenticated) return;
    const defaultArtist = user.username?.trim();
    if (!defaultArtist || defaultArtist === "guest") return;

    for (const p of Object.values(items)) {
      if (artistPrefillDoneRef.current.has(p.pixel)) continue;
      const current = p.customer?.artistName?.trim();
      if (current) {
        artistPrefillDoneRef.current.add(p.pixel);
        continue;
      }
      artistPrefillDoneRef.current.add(p.pixel);
      void patchItem(p.pixel, (cur) => ({
        ...cur,
        customer: { ...cur.customer, artistName: defaultArtist },
      }));
    }
  }, [isAuthenticated, user.username, items, patchItem]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [targetPixelForEditor, setTargetPixelForEditor] = useState<number | null>(
    null,
  );

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    setPendingFile(null);
    setTargetPixelForEditor(null);
  }, []);

  const coordsLabel = (p: Pixel) => {
    const lat =
      typeof p.info?.lat === "number"
        ? p.info.lat
        : getLatLngFromBlockNumber(...integerToBlock(p.pixel))[0];
    const lng =
      typeof p.info?.lng === "number"
        ? p.info.lng
        : getLatLngFromBlockNumber(...integerToBlock(p.pixel))[1];
    return (
      <>
        {convertDecimalToDMS(lat, "lat")}
        <br />
        {convertDecimalToDMS(lng, "lng")}
      </>
    );
  };

  const validateAndOpenEditor = (
    pixel: number,
    file: File | null | undefined,
  ) => {
    if (!file) return;
    if (!ACCEPT_IMAGE.includes(file.type)) {
      toast.show(t("cart.invalidFileType"), "danger");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.show(t("cart.fileTooLarge"), "danger");
      return;
    }
    setTargetPixelForEditor(pixel);
    setPendingFile(file);
    setEditorOpen(true);
  };

  const onEditorApply = async (dataUrl: string, fileName: string) => {
    if (targetPixelForEditor == null) return;
    const pid = targetPixelForEditor;
    await patchItem(pid, (cur) => ({
      ...cur,
      customer: {
        ...cur.customer,
        imageData: dataUrl,
        imageName: fileName,
        imageType: "image/jpeg",
      },
    }));
    toast.show(t("cart.imageSaved"), "success");
  };

  const handleRemove = async (p: Pixel) => {
    const label =
      p.customer?.frameName?.trim() ||
      `Pixel ${p.pixel}`;
    if (
      typeof window !== "undefined" &&
      !window.confirm(t("cart.confirmRemove", { label }))
    ) {
      return;
    }

    const r = await unreserve(p.pixel);
    if (!r.ok && r.error) {
      toast.show(r.error, "danger");
    } else if (r.ok) {
      toast.show(t("cart.removed"), "success");
    }
  };

  return (
    <section className="content dwik-cart-page">
      <h1>{t("cart.title")}</h1>
      <PurchaseGuide kind="cart" enabled={count > 0} />
      <label className="cart-stay-preference"><input type="checkbox" checked={stayOnMap} onChange={e => {
        setStayOnMap(e.target.checked);
      }} /> {t("guide.stay")}</label>
      <h3 style={{ color: "#474747" }}>
        {t("cart.aiHint")}{" "}
        <a
          href="https://www.mage.space"
          target="_blank"
          rel="noreferrer"
          style={{ color: "#474747" }}
        >
          <b>www.mage.space</b>
        </a>
      </h3>

      <CartPhotoEditorModal
        open={editorOpen}
        file={pendingFile}
        onClose={closeEditor}
        onApply={onEditorApply}
      />

      <div className="rahmen-shop-wrapper">
        <div className="rahmen-grid">
          {count === 0 ? (
            <p style={{ textAlign: "center", padding: "2em" }}>
              {t("cart.empty")}
            </p>
          ) : (
            Object.values(items).map((p) => (
              <CartRahmenCard
                key={p.pixel}
                pixel={p}
                patchItem={patchItem}
                onRemove={() => void handleRemove(p)}
                coordsLabel={coordsLabel(p)}
                onPickImageFile={(file) =>
                  validateAndOpenEditor(p.pixel, file ?? undefined)
                }
              />
            ))
          )}
        </div>

        <hr />
        <div className="rahmen-abschluss">
          <p className="right">
            <strong>{t("cart.total")}</strong>{" "}
            <span id="rahmen-summe">{formatMoney(total)}</span>
          </p>
          <div className="btn-row">
            <a
              className="btn-weiter-einkaufen"
              onClick={() => navigate("/world")}
              style={{ cursor: "pointer" }}
            >
              {t("cart.continueShopping")}
            </a>
            {count > 0 ? (
              <a
                className="btn-kaufen"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/payment");
                }}
                style={{ cursor: "pointer" }}
              >
                {t("cart.checkout")}
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function CartRahmenCard(props: {
  pixel: Pixel;
  patchItem: (
    pid: number,
    updater: (previous: Pixel) => Pixel,
  ) => Promise<void>;
  coordsLabel: ReactNode;
  onRemove: () => void;
  onPickImageFile: (file: File | null) => void;
}) {
  const { pixel: p, patchItem, coordsLabel, onRemove, onPickImageFile } =
    props;
  const { t, formatMoney, currency } = useLocale();
  const unitPrice = resolvePrice(p.region?.properties?.price, currency);
  const c = p.customer;
  const frameName = c?.frameName ?? "";
  const artistName = c?.artistName ?? "";
  const frameColor = c?.frameColor ?? "#000000";
  const imgSrc = c?.imageData?.length ? c.imageData : DEFAULT_FRAME_IMG;

  const onUploadChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    onPickImageFile(file);
    e.target.value = "";
  };

  return (
    <div
      className="rahmen-card"
      data-price={unitPrice}
      data-pixel={p.pixel}
    >
      <div className="image-wrap">
        <img
          className="rahmen-image"
          src={imgSrc}
          alt={t("cart.frameAlt")}
          style={{
            width: "100%",
            height: 220,
            objectFit: "cover",
            border: `3px solid ${frameColor}`,
            borderRadius: 12,
          }}
        />
        <label className="upload-overlay" title={t("cart.uploadImage")}>
          <input
            className="file-input"
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            hidden
            onChange={onUploadChange}
          />
          <span className="upload-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                fill="currentColor"
                d="M19 15v4H5v-4H3v6h18v-6h-2zM11 3v9.17l-3.59-3.58L6 10l6 6 6-6-1.41-1.41L13 12.17V3h-2z"
              />
            </svg>
          </span>
          <span className="upload-text">{t("cart.swapImage")}</span>
        </label>
      </div>

      <div className="rahmen-card-content">
        <div style={{ display: "flex", flexWrap: "nowrap", width: "100%" }}>
          <label className="field" style={{ flex: "1 1 auto" }}>
            <span>
              <strong>{t("cart.frameName")}</strong>
            </span>
            <input
              className="frame-name"
              type="text"
              placeholder={t("cart.frameNamePlaceholder")}
              value={frameName}
              onChange={(e) =>
                void patchItem(p.pixel, (cur) => ({
                  ...cur,
                  customer: {
                    ...cur.customer,
                    frameName: e.target.value,
                  },
                }))
              }
            />
          </label>

          <label className="field color-field">
            <span>
              <strong>{t("common.color")}:</strong>
            </span>
            <input
              className="frame-color"
              type="color"
              value={frameColor}
              aria-label={t("cart.frameColor")}
              onChange={(e) => {
                const v = e.target.value;
                void patchItem(p.pixel, (cur) => ({
                  ...cur,
                  customer: { ...cur.customer, frameColor: v },
                }));
              }}
            />
            <span className="color-hex">{frameColor}</span>
          </label>
        </div>

        <label className="field" style={{ width: "100%" }}>
          <span>
            <strong>{t("cart.artist")}</strong>
          </span>
          <input
            className="artist-name"
            type="text"
            placeholder={t("common.name")}
            value={artistName}
            onChange={(e) =>
              void patchItem(p.pixel, (cur) => ({
                ...cur,
                customer: {
                  ...cur.customer,
                  artistName: e.target.value,
                },
              }))
            }
          />
        </label>

        <div style={{ flex: "1 1 100%", minWidth: 200 }}>
          <p>
            <strong>{t("common.pixelId")}:</strong> <span>{p.pixel}</span>
          </p>
          <p>
            <strong>{t("common.coordinates")}:</strong>{" "}
            <span className="coords">{coordsLabel}</span>
          </p>
          <p>
            <strong>{t("common.address")}:</strong>{" "}
            <span className="address">{p.location || t("common.dash")}</span>
          </p>
          <p>
            <strong>{t("common.price")}:</strong>{" "}
            <span className="price">
              {formatMoney(unitPrice)}
            </span>
          </p>
          <button className="btn-delete" type="button" onClick={onRemove}>
            {t("common.remove")}
          </button>
        </div>
      </div>
    </div>
  );
}
